import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowDown, MoveDown } from "lucide-react";

const lineAnim = (i) => ({
  initial: { y: "110%" },
  animate: { y: "0%" },
  transition: { type: "spring", stiffness: 60, damping: 16, delay: 0.45 + i * 0.16 },
});

export const Hero = ({ t }) => {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const glowY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const go = (hash) => (e) => {
    e.preventDefault();
    lenis?.scrollTo(hash, { offset: -72 });
  };

  return (
    <section id="top" ref={ref} className="relative flex min-h-screen flex-col justify-end overflow-hidden pt-[72px]" data-testid="hero-section">
      <div className="bg-grid absolute inset-0" />
      <motion.div
        style={{ y: glowY }}
        className="pointer-events-none absolute -top-40 right-[-10%] h-[70vh] w-[70vh] rounded-full bg-[#00e5ff]/15 blur-[140px]"
      />
      <motion.div
        style={{ y: imgY }}
        className="pointer-events-none absolute bottom-0 right-0 z-0 hidden w-[42vw] max-w-[720px] lg:block"
      >
        <div className="spotlight clipped-frame relative translate-y-10 border-b-0">
          <img src="/assets/container-concept.png" alt="SQB Container autonomous store" className="w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#020617] via-transparent to-transparent" />
        </div>
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-[1440px] px-5 pb-24 md:px-10 md:pb-32">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.25 }}
          className="mb-8 flex items-center gap-3 font-mono-spec text-[11px] uppercase tracking-[0.3em] text-[#00e5ff]"
          data-testid="hero-eyebrow"
        >
          <span className="inline-block h-px w-10 bg-[#00e5ff]" />
          {t.eyebrow}
        </motion.p>

        <h1 className="font-display font-extrabold uppercase leading-[0.95] tracking-tighter" data-testid="hero-headline">
          {t.lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                {...lineAnim(i)}
                className={`block text-[13vw] md:text-[8vw] ${i === t.lines.length - 1 ? "text-outline" : "text-slate-50"}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-base leading-relaxed text-slate-400 md:text-lg"
          data-testid="hero-sub"
        >
          {t.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#machines"
            onClick={go("#machines")}
            data-testid="hero-cta-primary"
            className="group flex items-center gap-3 rounded-full bg-[#00e5ff] px-7 py-3.5 font-mono-spec text-xs uppercase tracking-[0.2em] text-slate-950 transition-transform duration-300 hover:-translate-y-1"
          >
            {t.ctaPrimary}
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
          </a>
          <a
            href="#contact"
            onClick={go("#contact")}
            data-testid="hero-cta-secondary"
            className="rounded-full border border-white/15 px-7 py-3.5 font-mono-spec text-xs uppercase tracking-[0.2em] text-slate-300 transition-all duration-300 hover:-translate-y-1 hover:border-[#00e5ff]/60 hover:text-[#00e5ff]"
          >
            {t.ctaSecondary}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.6 }}
          className="mt-14 flex flex-wrap gap-3"
        >
          {t.chips.map((chip, i) => (
            <span
              key={chip}
              data-testid={`hero-chip-${i}`}
              className={`animate-float rounded-full border border-white/10 bg-slate-900/60 px-4 py-1.5 font-mono-spec text-[10px] uppercase tracking-[0.25em] text-slate-300 backdrop-blur-md`}
              style={{ animationDelay: `${i * 0.8}s` }}
            >
              {chip}
            </span>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 right-5 z-10 hidden items-center gap-2 font-mono-spec text-[10px] uppercase tracking-[0.3em] text-slate-500 md:right-10 lg:flex"
      >
        {t.scroll}
        <MoveDown className="h-3.5 w-3.5 animate-bounce" />
      </motion.div>
    </section>
  );
};
